import React, { useEffect, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Slack, Mail, FileText } from 'lucide-react';
export function Hero() {
  const [typedText, setTypedText] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const fullQuery = "What did Sarah say about the Q3 roadmap in last week's sync?";
  useEffect(() => {
    let i = 0;
    const typingInterval = setInterval(() => {
      if (i < fullQuery.length) {
        setTypedText(fullQuery.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => setShowResponse(true), 600);
      }
    }, 40);
    return () => clearInterval(typingInterval);
  }, []);
  return <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden" data-id="element-101">
      {/* Subtle radial gradient background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-cozy-accent/5 rounded-full blur-[100px] -z-10 pointer-events-none" data-id="element-102" />

      <div className="max-w-6xl mx-auto px-6" data-id="element-103">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-104">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-105">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cozy-accent/10 text-cozy-accent text-sm font-medium mb-6 border border-cozy-accent/20" data-id="element-106">
              <Sparkles className="w-4 h-4" data-id="element-107" />
              Introducing GetCozy 2.0
            </span>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.1] tracking-tight mb-6 text-cozy-text" data-id="element-108">
              Your AI, finally with memory that feels{' '}
              <span className="italic text-cozy-accent" data-id="element-109">like home.</span>
            </h1>
            <p className="text-lg md:text-xl text-cozy-muted mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-110">
              Capture conversations, actions, and documents across all your
              tools. Ask anything in natural language. Turn recurring workflows
              into reusable skills.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-111">
              <a href="#start" className="w-full sm:w-auto bg-cozy-text text-white px-6 py-3 rounded-full font-medium hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-112">
                Get started for free
              </a>
              <a href="#demo" className="w-full sm:w-auto bg-white border border-cozy-border text-cozy-text px-6 py-3 rounded-full font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-113">
                View demo <ArrowRight className="w-4 h-4" data-id="element-114" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Terminal / Query Card Demo */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="max-w-4xl mx-auto bg-white rounded-2xl border border-cozy-border shadow-xl shadow-black/5 overflow-hidden" data-id="element-115">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-cozy-border bg-cozy-bg/50" data-id="element-116">
            <div className="flex gap-1.5" data-id="element-117">
              <div className="w-3 h-3 rounded-full bg-red-400/80" data-id="element-118" />
              <div className="w-3 h-3 rounded-full bg-amber-400/80" data-id="element-119" />
              <div className="w-3 h-3 rounded-full bg-green-400/80" data-id="element-120" />
            </div>
            <div className="mx-auto text-xs font-mono text-cozy-muted" data-id="element-121">
              getcozy-query-engine
            </div>
          </div>

          <div className="p-6 md:p-8 font-mono text-sm md:text-base" data-id="element-122">
            <div className="flex gap-4 mb-6" data-id="element-123">
              <span className="text-cozy-accent shrink-0" data-id="element-124">❯</span>
              <span className="text-cozy-text" data-id="element-125">
                {typedText}
                {!showResponse && <span className="animate-pulse inline-block w-2 h-5 bg-cozy-accent ml-1 align-middle" data-id="element-126" />}
              </span>
            </div>

            {showResponse && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="pl-6 border-l-2 border-cozy-border/50 text-cozy-muted space-y-4" data-id="element-127">
                <p className="leading-relaxed" data-id="element-128">
                  Based on the sync last Thursday, Sarah mentioned that the Q3
                  roadmap will prioritize{' '}
                  <strong className="text-cozy-text font-medium" data-id="element-129">
                    Enterprise SSO
                  </strong>{' '}
                  and{' '}
                  <strong className="text-cozy-text font-medium" data-id="element-130">
                    Custom RBAC
                  </strong>
                  . She noted that the mobile app redesign is being pushed to Q4
                  due to resource constraints.
                </p>

                <div className="flex flex-wrap gap-2 pt-2" data-id="element-131">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cozy-bg border border-cozy-border text-xs text-cozy-text" data-id="element-132">
                    <Slack className="w-3 h-3 text-blue-500" data-id="element-133" /> #eng-leadership
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cozy-bg border border-cozy-border text-xs text-cozy-text" data-id="element-134">
                    <FileText className="w-3 h-3 text-blue-600" data-id="element-135" /> Q3 Planning
                    Doc
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cozy-bg border border-cozy-border text-xs text-cozy-text" data-id="element-136">
                    <Mail className="w-3 h-3 text-red-500" data-id="element-137" /> Fwd: Roadmap
                    Update
                  </span>
                </div>
              </motion.div>}
          </div>
        </motion.div>

        {/* Trusted By */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="mt-16 text-center" data-id="element-138">
          <p className="text-xs font-medium text-cozy-muted uppercase tracking-wider mb-6" data-id="element-139">
            Trusted by forward-thinking teams
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-50 grayscale" data-id="element-140">
            {/* Fake logos using text for simplicity, but styled to look like wordmarks */}
            <span className="font-serif text-xl font-bold" data-id="element-141">Acme Corp</span>
            <span className="font-sans text-xl font-black tracking-tighter" data-id="element-142">
              GLOBEX
            </span>
            <span className="font-mono text-lg font-bold" data-id="element-143">Initech</span>
            <span className="font-serif text-xl italic" data-id="element-144">Soylent</span>
            <span className="font-sans text-xl font-bold tracking-widest" data-id="element-145">
              UMBRELLA
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}